import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  Badge,
  Box,
  BreadcrumbItem,
  BreadcrumbPage,
  Button,
  createColumnHelper,
  DataTable,
  DataTableCellContent,
  DataTableColumnSettings,
  DataTableEmpty,
  Icon,
  MenuItem,
  MenuSeparator,
  Text,
  useIsMobile,
  type DataTableColumnDef,
  type DataTableGroupType,
  type SortingState,
} from "@salestrak/ui";
import { PageHeader, PageHeaderActions, PageHeaderBreadcrumb } from "#app/components/page-header";
import { ExpandableSearch } from "@salestrak/ui";

import { useTrashMutations, getTrashConnection, trashDaysRemaining } from "#app/hooks/settings/use-trash-mutations";
import { GetTrashDocument, type TrashRow } from "#app/lib/data";
import { TrashConfirmDialog } from "#app/components/settings/trash-confirm-dialog";
import { EntityTag } from "#app/components/entity";
import { GroupQueryProvider, useGroupStates } from "#app/lib/hooks/group-query-provider";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { withPermission } from "#app/components/permissions/require-permission";

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const h = createColumnHelper<TrashRow>();

// Shared by the table (sticky pinning) and the column settings panel (locked
// rows) — the primary identifying column is locked like sibling tables.
const TRASH_PINNED_COLUMN_IDS = ["name"];

// Restore and delete are the bulk-action bar's and the row menu's — the
// columns render the entity and its countdown, nothing actionable.
function useTrashColumns(): DataTableColumnDef<TrashRow>[] {
  return useMemo<DataTableColumnDef<TrashRow>[]>(
    () => [
      h.display({
        id: "name",
        header: "Name",
        enableSorting: false,
        meta: { minWidth: 250, truncate: true },
        cell: ({ row }) => {
          const item = row.original;
          return (
            <DataTableCellContent>
              <Text size="sm" weight="medium" truncate={1}>
                {item.name}
              </Text>
            </DataTableCellContent>
          );
        },
      }),
      h.display({
        id: "trashedByUser",
        header: "Deleted By",
        enableSorting: false,
        meta: { minWidth: 180 },
        cell: ({ row }) => {
          const user = row.original.trashedByUser;
          if (!user) {
            return (
              <DataTableCellContent>
                <Text size="sm" variant="muted">Unknown</Text>
              </DataTableCellContent>
            );
          }
          return (
            <DataTableCellContent>
              {/* Static — a trashed row is history, not a live entity. */}
              <EntityTag
                entity={{ kind: "user", id: user.userId, user }}
                interactive={false}
              />
            </DataTableCellContent>
          );
        },
      }),
      h.date({
        accessorKey: "trashedAt",
        header: "Deleted On",
      }),
      h.display({
        id: "daysRemaining",
        header: "Auto-delete",
        enableSorting: false,
        meta: { minWidth: 120 },
        cell: ({ row }) => {
          const days = trashDaysRemaining(row.original.trashedAt);
          return (
            <DataTableCellContent>
              {/* Badge tone is a single variant: the urgent window reads as
                  the soft destructive wash, everything else a plain outline. */}
              <Badge size="sm" variant={days <= 7 ? "error" : "outline"}>
                <Text>{days} day{days !== 1 ? "s" : ""}</Text>
              </Badge>
            </DataTableCellContent>
          );
        },
      }),
    ],
    [],
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function TrashPage() {
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [sorting, setSorting] = useState<SortingState>([
    { id: "trashedAt", desc: true },
  ]);

  const isMobile = useIsMobile();
  const { states, registerState, getGroupProps } = useGroupStates<TrashRow>();
  const groupState = states.get("__all__");
  const total = groupState?.total ?? 0;

  const queryVariables = useMemo(
    () => ({
      limit: 50,
      where: debouncedSearch ? { search: debouncedSearch } : undefined,
    }),
    [debouncedSearch],
  );

  const { restoreItem, deleteItemPermanently, clearAllTrash } = useTrashMutations();

  // --- Clear all dialog ---
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const clearAllLabel = debouncedSearch
    ? "Clear filtered items"
    : "Clear all trash";

  const clearAllDescription = debouncedSearch
    ? `Are you sure you want to permanently delete all items matching "${debouncedSearch}"? This action cannot be undone.`
    : `Are you sure you want to permanently delete all ${total} item${total !== 1 ? "s" : ""} in trash? This action cannot be undone.`;

  const handleClearAll = useCallback(async () => {
    const where = debouncedSearch ? { search: debouncedSearch } : undefined;
    await clearAllTrash(where);
  }, [clearAllTrash, debouncedSearch]);

  // --- Delete permanently dialog ---
  const [deleteTarget, setDeleteTarget] = useState<TrashRow | null>(null);

  const handleDeletePermanently = useCallback((item: TrashRow) => {
    setDeleteTarget(item);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteItemPermanently(deleteTarget.id);
    setDeleteTarget(null);
  }, [deleteTarget, deleteItemPermanently]);

  // --- Restore ---
  const handleRestore = useCallback(
    async (item: TrashRow) => {
      await restoreItem(item.id);
    },
    [restoreItem],
  );

  const columns = useTrashColumns();
  const tableView = useTableViewSettings("trash");

  const groups = useMemo<DataTableGroupType<TrashRow>[]>(
    () => [
      {
        key: "__all__",
        label: "All",
        totalCount: total,
        ...getGroupProps("__all__"),
      },
    ],
    [total, getGroupProps],
  );

  return (
    <Box direction="column" fill="flex">
      <GroupQueryProvider<TrashRow>
        groupKey="__all__"
        document={GetTrashDocument}
        variables={queryVariables}
        getConnection={getTrashConnection}
        onStateChange={registerState}
        fetchPolicy="network-only"
      />
      <PageHeader>
        <PageHeaderBreadcrumb>
          <BreadcrumbItem>
            {/* Raw string — BreadcrumbPage carries the page-title type and
                truncates; a nested default-size Text shrinks it to sm on
                desktop. */}
            <BreadcrumbPage>
              {total > 0
                ? `Trash · ${total}${!isMobile ? ` ${total === 1 ? "item" : "items"}` : ""}`
                : "Trash"}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </PageHeaderBreadcrumb>
        <PageHeaderActions>
          <ExpandableSearch
            onValueChange={setDebouncedSearch}
            placeholder="Search trash..."
            size="xs"
          />
          <DataTableColumnSettings
            columns={columns}
            pinnedColumnIds={TRASH_PINNED_COLUMN_IDS}
            {...tableView.settingsProps}
          />
          {total > 0 && (
            <Button
              size="xs"
              variant="destructive-outline"
              onClick={() => setClearDialogOpen(true)}
            >
              <Icon name="trash-2" />
              <Text>{debouncedSearch ? "Clear filtered" : "Clear all"}</Text>
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <Box direction="column" fill="flex" paddingBottom="lg">
        <DataTable
          groups={groups}
          columns={columns}
          getRowId={(r) => r.id}
          sorting={sorting}
          onSortingChange={setSorting}
          density="regular"
          stickyHeader
          fillColumnId="name"
          pinnedColumnIds={TRASH_PINNED_COLUMN_IDS}
          paddingX="md"
          {...tableView.tableProps}
          renderRowActions={(item) => (
            <>
              <MenuItem onClick={() => handleRestore(item)}>
                <Icon name="refresh-cw" size="lg" color="muted" />
                Restore
              </MenuItem>
              <MenuSeparator />
              <MenuItem
                variant="destructive"
                onClick={() => handleDeletePermanently(item)}
              >
                <Icon name="trash-2" size="lg" />
                Delete permanently...
              </MenuItem>
            </>
          )}
          emptyState={
            <DataTableEmpty>
              <Box gap="xs" align="center">
                <Text size="sm" variant="muted">
                  {debouncedSearch
                    ? "No items match your search"
                    : "Trash is empty"}
                </Text>
                <Text size="xs" variant="muted">
                  {debouncedSearch
                    ? "Try adjusting your search terms"
                    : "Items you delete will appear here for 30 days"}
                </Text>
              </Box>
            </DataTableEmpty>
          }
        />
      </Box>

      {/* Clear all confirmation */}
      <TrashConfirmDialog
        open={clearDialogOpen}
        onOpenChange={setClearDialogOpen}
        title={clearAllLabel}
        description={clearAllDescription}
        confirmLabel={debouncedSearch ? "Clear filtered" : "Clear all"}
        onConfirm={handleClearAll}
      />

      {/* Delete permanently confirmation */}
      <TrashConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Permanently delete item"
        description={
          deleteTarget
            ? `Are you sure you want to permanently delete "${deleteTarget.name}"? This action cannot be undone and will delete all related data.`
            : ""
        }
        confirmLabel="Delete permanently"
        onConfirm={confirmDelete}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/trash")({
  component: withPermission("trash.manage", TrashPage),
});
